import React, { ChangeEvent, useEffect, useState } from 'react';
import Button from 'src/components/Button';
import RadioButtonGroup from 'src/components/RadioButtonGroup/RadioButtonGroup';
import FontAwesomeIcon from 'src/components/Icon';
import { ISuite } from 'src/components/SharedTypes';
import { IBulkWalkthroughRequest } from 'src/services/client-types';
import BulkWalkthroughsSuiteTable from 'src/components/BulkOperations/BulkWalkthroughsSuiteTable/BulkWalkthroughsSuiteTable';
import { filterUpdatedSelectedSuiteIds } from 'src/components/BulkOperations/helpers';
import './BulkAddWalkthroughs.scss';

interface BulkAddWalkthroughsProps {
  userId: string;
  selectedSuiteIds: string[];
  onSave?: (params: any) => void;
  suites: ISuite[];
}

const BulkAddWalkthroughs = ({
  userId,
  selectedSuiteIds: initialSelectedSuiteIds,
  onSave,
  suites
}: BulkAddWalkthroughsProps) => {
  const [walkthroughName, setWalkthroughName] = useState('');
  const [description, setDescription] = useState('');
  const [walkthroughType, setWalkthroughType] = useState('');
  const [permissionType, setPermissionType] = useState('');
  const [checklistItems, setChecklistItems] = useState<string[]>(['']);
  const [selectedSuiteIds, setSelectedSuiteIds] = useState<string[]>(
    initialSelectedSuiteIds
  );

  const walkthroughTypeOptions: any = [
    { value: 'Simplified', label: 'Simplified' },
    { value: 'Enhanced', label: 'Enhanced' },
  ];

  const permissionTypeOptions: any = [
    { value: 'Internal', label: 'Internal' },
    { value: 'Homeowner or Occupant', label: 'Homeowner or Occupant' },
  ];

  // Only set the initial selection if suiteIds were actually passed in.
  useEffect(() => {
    if (initialSelectedSuiteIds.length > 0) {
      setSelectedSuiteIds(initialSelectedSuiteIds);
    }
  }, [initialSelectedSuiteIds]);

  const handleSuiteSelect = (suiteId: string) => {
    const updatedSelectedSuiteIds: string[] = filterUpdatedSelectedSuiteIds(
      suiteId,
      selectedSuiteIds
    );

    setSelectedSuiteIds(updatedSelectedSuiteIds);
  };

  const handleSelectAllSuites = () => {
    const allSuitesToSelect: string[] = suites.map((suite: ISuite) => suite.id);
    setSelectedSuiteIds(allSuitesToSelect);
  };

  const handleDeselectAllSuites = () => {
    setSelectedSuiteIds([]);
  };

  const handleAddChecklistItem = () => {
    setChecklistItems([...checklistItems, '']);
  };

  const handleRemoveChecklistItem = (index: number) => {
    setChecklistItems(checklistItems.filter((_, i) => i !== index));
  };

  const handleChecklistItemChange = (index: number, value: string) => {
    const updatedItems: string[] = checklistItems.map((item, i) =>
      i === index ? value : item
    );

    setChecklistItems(updatedItems);
  };

  const hasEmptyChecklistItems = () => {
    if (walkthroughType === 'Enhanced') {
      return checklistItems.some((item: string) => !item.trim());
    }

    return false;
  };

  const isChecklistItemEmpty = (index: number) => {
    return walkthroughType === 'Enhanced' && !checklistItems[index]?.trim();
  };

  const isFormValid = () => {
    const basicValidation =
      walkthroughName.trim() &&
      walkthroughType &&
      permissionType &&
      selectedSuiteIds.length > 0;

    // For Enhanced walkthroughs, also check that no checklist items are empty.
    if (walkthroughType === 'Enhanced') {
      return basicValidation && !hasEmptyChecklistItems();
    }

    return basicValidation;
  };

  const saveBulkWalkthroughs = () => {
    const payload: IBulkWalkthroughRequest = {
      walkthrough: {
        title: walkthroughName,
        description: description,
        walkthroughType: walkthroughType,
        walkthroughName: walkthroughName,
        permissions: permissionType,
        userId: userId,
        status: walkthroughType === 'Enhanced' ? 'Activate' : 'Configure',
      },
      checklistItems:
        walkthroughType === 'Enhanced'
          ? checklistItems.filter((item: string) => item.trim())
          : [],
      units: selectedSuiteIds,
      walkthroughPermissions: permissionType,
    };

    onSave(payload);
  };

  return (
    <div className="walkthrough-form-container">
      <div className="walkthrough-form-content">
        <div className="walkthrough-form-row">
          <div className="walkthrough-form-column">
            <div className="walkthrough-input-group">
              <label className="walkthrough-form-label">
                Name<span className="required-field-indicator">*</span>
              </label>
              <input
                type="text"
                className="walkthrough-text-input"
                value={walkthroughName}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setWalkthroughName(e.target.value)
                }
                placeholder="Walkthrough Name"
                required
              />
            </div>
            {/* Walkthrough Type and Permissions Row. */}
            <div className="walkthrough-radio-groups-container">
              <RadioButtonGroup
                name="permissionType"
                options={permissionTypeOptions}
                value={permissionType}
                onChange={setPermissionType}
                label="Permissions:"
                required={true}
                className="walkthrough-radio-group"
              />
              <RadioButtonGroup
                name="walkthroughType"
                options={walkthroughTypeOptions}
                value={walkthroughType}
                onChange={setWalkthroughType}
                label="Walkthrough Type:"
                required={true}
                className="walkthrough-radio-group"
              />
            </div>
            {/* Enhanced Walkthrough Checklist Items. */}
            {walkthroughType === 'Enhanced' && (
              <div className="walkthrough-input-group">
                <label className="walkthrough-form-label">
                  Checklist Items
                  <span className="required-field-indicator">*</span>
                </label>
                <div className="walkthrough-checklist-section">
                  {checklistItems.map((item: string, index: number) => (
                    <div key={index} className="walkthrough-checklist-item-row">
                      <div className="walkthrough-checklist-item-container">
                        <input
                          type="text"
                          className={`walkthrough-text-input ${
                            isChecklistItemEmpty(index)
                              ? 'walkthrough-input-error'
                              : ''
                          }`}
                          value={item}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            handleChecklistItemChange(index, e.target.value)
                          }
                          placeholder={`Checklist item ${index + 1}`}
                        />
                        {isChecklistItemEmpty(index) && (
                          <div className="walkthrough-error-message">
                            Checklist item cannot be empty
                          </div>
                        )}
                      </div>
                      {checklistItems.length > 1 && (
                        <button
                          type="button"
                          className="checklist-item-remove-button"
                          onClick={() => handleRemoveChecklistItem(index)}
                        >
                          <FontAwesomeIcon glyph="trash" weight="light" />
                        </button>
                      )}
                    </div>
                  ))}
                  <Button
                    color="teal"
                    size="small"
                    label="+ Add Checklist Item"
                    onClick={handleAddChecklistItem}
                  />
                </div>
              </div>
            )}
            {/* Description. */}
            <div className="walkthrough-input-group">
              <label className="walkthrough-form-label">Description</label>
              <textarea
                className="walkthrough-description-textarea"
                value={description}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                  setDescription(e.target.value)
                }
                placeholder="Description"
                rows={3}
              />
            </div>
            {/* Scrollable Suite Selection Table Section. */}
            <div className="walkthrough-input-group">
              <label className="walkthrough-form-label">
                Choose Suites for Walkthrough ({selectedSuiteIds.length} of{' '}
                {suites.length} selected)
              </label>
              <div className="walkthrough-suite-table-section">
                <div className="walkthrough-suite-table-wrapper">
                  <BulkWalkthroughsSuiteTable
                    suites={suites}
                    selectedSuiteIds={selectedSuiteIds}
                    onSuiteSelect={handleSuiteSelect}
                    onSelectAll={handleSelectAllSuites}
                    onDeselectAll={handleDeselectAllSuites}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Actions - Now outside the scrollable area. */}
      <div className="walkthrough-form-actions">
        <Button
          color="teal"
          size="small"
          label={`Save Walkthroughs (${selectedSuiteIds.length} suite${selectedSuiteIds.length !== 1 ? 's' : ''})`}
          disabled={!isFormValid()}
          onClick={saveBulkWalkthroughs}
        />
      </div>
    </div>
  );
};

export default BulkAddWalkthroughs;
